import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Envelope, Phone, UserCircle, Clock, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import '../pages/ProviderDashboard.css';

const canAccessUsersManagement = (roles = []) =>
  roles.some((role) => ['admin', 'kcs-super-user'].includes((role || '').toLowerCase()));

const Workers = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });

  const hasAuthUser = Boolean(user);
  const userRoles = user?.userRoles || [];
  const isAdmin = canAccessUsersManagement(userRoles);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/provider/dashboard', { replace: true });
    }
  }, [authLoading, user, isAdmin, navigate]);

  const getUserEmail = (currentUser) => {
    const emailClaim = currentUser?.claims?.find((claim) =>
      ['email', 'emails', 'preferred_username', 'upn'].includes(claim.typ)
    )?.val;
    return (emailClaim || '').toLowerCase();
  };

  const isSelfWorker = (worker) => {
    if (!hasAuthUser) {
      return true;
    }

    const workerEmail = (worker.email || '').toLowerCase();
    const userEmail = getUserEmail(user);
    return Boolean(workerEmail && userEmail && workerEmail === userEmail);
  };

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const response = await fetch('/api/workers');
        if (!response.ok) throw new Error('Failed to fetch workers');
        const data = await response.json();
        setWorkers(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkers();
  }, []);

  const getSortValue = (worker, key) => {
    switch (key) {
      case 'firstName':
        return (worker.firstName || '').toLowerCase();
      case 'lastName':
        return (worker.lastName || '').toLowerCase();
      case 'type':
        return (worker.type || '').toLowerCase();
      case 'email':
        return (worker.email || '').toLowerCase();
      case 'phone':
        return (worker.phone || '').toLowerCase();
      case 'status':
        return worker.status === 'Active' ? 1 : 0;
      default:
        return '';
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedWorkers = [...workers].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading || authLoading) return <div className="loading">Loading users...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="provider-dashboard">
      <div className="page-header">
        <h1>Users</h1>
        {isAdmin && (
          <Link to="/provider/workers/new" className="action-button btn-solid-blue">
            Add User
          </Link>
        )}
      </div>

      <div className="table-section">
        {/* Mobile Card View */}
        <div className="mobile-cards">
          {sortedWorkers.map((worker) => (
            <div key={worker.id} className="service-card">
              <div className="card-header">
                <h3 className="card-customer">{worker.firstName} {worker.lastName}</h3>
                <span className={`status-badge ${worker.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                  {worker.status}
                </span>
              </div>
              <div className="card-details-compact">
                <div className="card-info-item">
                  <UserCircle size={16} weight="regular" className="card-icon" />
                  <span>{worker.providerSuperAdmin ? 'Provider Super Admin' : worker.type}</span>
                </div>
                <div className="card-info-item">
                  <Envelope size={16} weight="regular" className="card-icon" />
                  <span>{worker.email}</span>
                </div>
                <div className="card-info-item">
                  <Phone size={16} weight="regular" className="card-icon" />
                  <span>{worker.phone}</span>
                </div>
              </div>
              <div className="card-actions">
                {(isAdmin || isSelfWorker(worker)) && (
                  <Link to={`/provider/workers/${worker.id}/edit`} className="action-button btn-icon" title="Edit Worker">
                    <PencilSimple size={24} weight="regular" />
                  </Link>
                )}
                <Link to={`/provider/schedule?worker=${worker.id}`} className="action-button btn-icon" title="View Schedule">
                  <Clock size={24} weight="regular" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort('firstName')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>First Name {getSortIndicator('firstName')}</button></th>
                <th><button type="button" onClick={() => handleSort('lastName')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Last Name {getSortIndicator('lastName')}</button></th>
                <th><button type="button" onClick={() => handleSort('type')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Type {getSortIndicator('type')}</button></th>
                <th><button type="button" onClick={() => handleSort('email')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Email {getSortIndicator('email')}</button></th>
                <th><button type="button" onClick={() => handleSort('phone')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Phone {getSortIndicator('phone')}</button></th>
                <th><button type="button" onClick={() => handleSort('status')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Status {getSortIndicator('status')}</button></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedWorkers.map((worker) => (
                <tr key={worker.id}>
                  <td>{worker.firstName}</td>
                  <td>{worker.lastName}</td>
                  <td>
                    <span className={`type-badge type-${worker.type.toLowerCase()}`}>
                      {worker.providerSuperAdmin ? 'Provider Super Admin' : worker.type}
                    </span>
                  </td>
                  <td>{worker.email}</td>
                  <td>{worker.phone}</td>
                  <td>
                    <span className={`status-badge ${worker.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                      {worker.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {(isAdmin || isSelfWorker(worker)) && (
                        <Link to={`/provider/workers/${worker.id}/edit`} className="action-button btn-icon" title="Edit Worker">
                          <PencilSimple size={24} weight="regular" />
                        </Link>
                      )}
                      <Link to={`/provider/schedule?worker=${worker.id}`} className="action-button btn-icon" title="View Schedule">
                        <Clock size={24} weight="regular" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Workers;
